import { getEnv } from "@/config/env";
import { postJson, defaultHeaders } from "@/api/http";
import type { Result } from "@/types/layout";
import type { PlanChangePreview } from "@/types/plans";

const CART_MEMBERSHIP_ENDPOINT = "/cart/membership-subscription";
const CART_CONFIRM_PLAN_CHANGE_ENDPOINT = "/cart/confirm-plan-change";
const CART_PREVIEW_PLAN_CHANGE_ENDPOINT = "/cart/preview-plan-change";
const CART_EXECUTE_PLAN_CHANGE_ENDPOINT = "/cart/execute-plan-change";
const MEMBER_CANCEL_MEMBERSHIP_ENDPOINT = "/member/subscription/cancel";
const MEMBER_RESUME_MEMBERSHIP_ENDPOINT = "/member/subscription/resume";
const MEMBER_REDEEM_DISCOUNT_ENDPOINT = "/member/subscription/redeem-discount";
const MEMBER_REMOVE_DISCOUNT_ENDPOINT = "/member/subscription/discount";

/**
 * Structured failure variants the FE distinguishes. Anything Stripe-side or
 * server-side that the user can't resolve falls back to `"generic"`.
 *
 * `not_paid_user` is returned by the preview/execute endpoints when the BE
 * can't find an existing Stripe subscription for the member. The FE uses it
 * as a defence-in-depth signal to fall back to the standard add-to-cart →
 * Stripe Checkout flow (typically only fires when the FE's entitlement
 * snapshot was stale, e.g. the sub was cancelled in another tab).
 *
 * `no_payment_method` (402) means the member has no card Stripe can charge, so
 * the prorated upgrade invoice can't be paid. Raised by the PREVIEW as well as
 * execute, deliberately: the modal's Confirm button is the only thing that
 * could fail on it, so the flow is stopped before that button is ever shown.
 * The FE's answer is the wallet — the Stripe customer portal adds a card.
 */
export type PlanChangeError =
  | {
      kind: "currency_mismatch";
      existingCurrency: string;
      tierIdentifier: string;
      message: string;
    }
  | { kind: "not_paid_user"; message: string }
  | { kind: "no_payment_method"; message: string }
  | { kind: "generic"; message: string };

export type PlanChangeResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: PlanChangeError };

interface ProblemDetails {
  detail?: string;
  errorCode?: string;
  existingCurrency?: string;
  tierIdentifier?: string;
  /** ValidationProblemDetails shape: field → list of error messages. */
  errors?: Record<string, string[]>;
}

/**
 * Pulls the most useful human-readable reason out of a ProblemDetails body.
 * Validation errors arrive under `errors` (a dict of field → messages); when
 * absent we fall back to `detail`. This is what gets surfaced to the user when
 * the BE can't honour the plan change so the failure isn't silent.
 */
function summariseProblem(problem: ProblemDetails): string | undefined {
  if (problem.errors) {
    const messages = Object.values(problem.errors).flat().filter(Boolean);
    if (messages.length > 0) return messages.join(" ");
  }
  return problem.detail;
}

/**
 * POSTs JSON and, on failure, attempts to parse a `ProblemDetails` body so the
 * caller can distinguish structured server errors (currency_mismatch) from
 * generic transport / 500-class failures. Used only by the plan-change flow
 * because the BE returns ProblemDetails with `extensions.errorCode` here.
 */
async function postWithProblemDetails<T>(
  url: string,
  body: unknown
): Promise<PlanChangeResult<T>> {
  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        // Without these the BE can't resolve which site / culture / store the
        // request belongs to, so MyAccountB2cPage lookups land on the wrong
        // node and tier identifiers don't match. Must mirror getJson/postJson.
        ...defaultHeaders(),
      },
      body: JSON.stringify(body),
    });
  } catch (err) {
    return {
      ok: false,
      error: {
        kind: "generic",
        message: err instanceof Error ? err.message : "Network error",
      },
    };
  }

  if (response.ok) {
    // 204 No Content (e.g. for confirm-plan-change with a void response) → return null cast.
    if (response.status === 204) {
      return { ok: true, data: null as unknown as T };
    }
    try {
      const data = (await response.json()) as T;
      return { ok: true, data };
    } catch {
      return { ok: true, data: null as unknown as T };
    }
  }

  // Try to parse a ProblemDetails body. If it parses and carries our error code,
  // return the structured variant; otherwise fall back to generic.
  try {
    const problem = (await response.json()) as ProblemDetails;
    if (
      problem.errorCode === "currency_mismatch" &&
      problem.existingCurrency &&
      problem.tierIdentifier
    ) {
      return {
        ok: false,
        error: {
          kind: "currency_mismatch",
          existingCurrency: problem.existingCurrency,
          tierIdentifier: problem.tierIdentifier,
          message: problem.detail ?? "Currency mismatch.",
        },
      };
    }
    if (problem.errorCode === "not_paid_user") {
      return {
        ok: false,
        error: {
          kind: "not_paid_user",
          message: problem.detail ?? "No existing paid subscription found.",
        },
      };
    }
    if (problem.errorCode === "no_payment_method") {
      return {
        ok: false,
        error: {
          kind: "no_payment_method",
          message: problem.detail ?? "No payment method is saved.",
        },
      };
    }
    const summary = summariseProblem(problem);
    return {
      ok: false,
      error: {
        kind: "generic",
        message: summary ?? `${response.status} ${response.statusText}`.trim(),
      },
    };
  } catch {
    return {
      ok: false,
      error: {
        kind: "generic",
        message: `${response.status} ${response.statusText}`.trim(),
      },
    };
  }
}

/**
 * Adds the chosen plan tier to the digital cart. May fail with a structured
 * `currency_mismatch` error when the user's existing Stripe subscription is in
 * a currency the tier doesn't have a Stripe Price configured for.
 *
 * `deviceOptionReference` is the selected device-count variant's
 * `licenseProductReference` GUID. When supplied, the BE re-resolves that option
 * server-side and adds its License + Stripe Price instead of the tier's base
 * product. Omit (or pass undefined) to add the tier's base product.
 */
export async function addMembershipToCart(
  tierIdentifier: string,
  deviceOptionReference?: string
): Promise<PlanChangeResult<null>> {
  const env = getEnv();
  const apiUrl = `${env.apiBaseUrl}${CART_MEMBERSHIP_ENDPOINT}`;
  const result = await postWithProblemDetails<unknown>(apiUrl, {
    tierIdentifier,
    deviceOptionReference,
  });
  return result.ok ? { ok: true, data: null } : result;
}

export interface ConfirmPlanChangeResponse {
  subscriptionId: string;
  newTierIdentifier: string;
  prorationBehavior: string;
}

/**
 * Finalises a plan change for a PAID user. Calls Stripe Subscription.Update on
 * the existing subscription with the new price, using proration.
 */
export async function confirmPlanChange(): Promise<
  Result<ConfirmPlanChangeResponse>
> {
  const env = getEnv();
  const apiUrl = `${env.apiBaseUrl}${CART_CONFIRM_PLAN_CHANGE_ENDPOINT}`;
  // Confirm uses the regular postJson path — its failure modes are Stripe-side
  // (declined card, invalid sub) and don't need structured FE handling beyond
  // the generic "couldn't change your plan" message.
  return postJson<ConfirmPlanChangeResponse>(apiUrl, {});
}

/**
 * Fetches a Stripe-sourced proration preview for changing the current user's
 * paid subscription to {tierIdentifier}. Returns `not_paid_user` when the BE
 * can't find an existing subscription (caller should fall back to the standard
 * add-to-cart flow) and `currency_mismatch` when the tier has no Stripe Price
 * configured in the existing subscription's currency.
 *
 * This endpoint is stateless — calling it does not modify the cart. Stripe
 * failures surface as 5xx generic errors; the modal can't render without
 * proration so the FE should keep the user on the tier picker with an error.
 */
export async function previewMembershipPlanChange(
  tierIdentifier: string,
  deviceOptionReference?: string
): Promise<PlanChangeResult<PlanChangePreview>> {
  const env = getEnv();
  const apiUrl = `${env.apiBaseUrl}${CART_PREVIEW_PLAN_CHANGE_ENDPOINT}`;
  return postWithProblemDetails<PlanChangePreview>(apiUrl, {
    tierIdentifier,
    deviceOptionReference,
  });
}

/**
 * Executes the plan change for a PAID user directly — calls Stripe
 * Subscription.Update with the prorated billing behaviour resolved server-side
 * (upgrade → always_invoice, downgrade → create_prorations). No cart involved.
 * Use this from the plan-change confirmation modal; for the cart-driven flow
 * (free → paid) use {@link addMembershipToCart} + standard checkout instead.
 */
export async function executeMembershipPlanChange(
  tierIdentifier: string,
  deviceOptionReference?: string
): Promise<PlanChangeResult<ConfirmPlanChangeResponse>> {
  const env = getEnv();
  const apiUrl = `${env.apiBaseUrl}${CART_EXECUTE_PLAN_CHANGE_ENDPOINT}`;
  return postWithProblemDetails<ConfirmPlanChangeResponse>(apiUrl, {
    tierIdentifier,
    deviceOptionReference,
  });
}

/**
 * Structured failures the cancel-membership FE distinguishes. `externally_managed`
 * is the BE's safety-net rejection for App Store / Play Store / Amazon entitlements
 * that slipped past the FE's pre-flight check (e.g. state changed between page
 * load and submit). `team_order` means the subscription bills several licences
 * (a legacy team order) and only support can cancel it — retrying cannot help.
 * `generic` is the catch-all for everything else.
 */
export type CancelMembershipError =
  | { kind: "externally_managed"; store: string | null; message: string }
  | { kind: "team_order"; message: string }
  | { kind: "generic"; message: string };

export type CancelMembershipResult =
  | { ok: true }
  | { ok: false; error: CancelMembershipError };

interface CancelMembershipProblemDetails {
  detail?: string;
  errorCode?: string;
  store?: string;
}

/**
 * Schedules cancellation of the user's current Stripe-managed membership at
 * the end of the current billing period. `reason` is an optional analytics tag
 * (e.g. "too-expensive") collected via the cancel UI; the BE logs it but does
 * not persist it. The BE rejects externally-managed entitlements with a 409 +
 * ProblemDetails `errorCode: "subscription_externally_managed"`; we parse that
 * out so the UI can refresh and show the right "manage on …" message.
 */
export async function cancelMembership(
  reason: string | null
): Promise<CancelMembershipResult> {
  const env = getEnv();
  const apiUrl = `${env.apiBaseUrl}${MEMBER_CANCEL_MEMBERSHIP_ENDPOINT}`;

  let response: Response;
  try {
    response = await fetch(apiUrl, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...defaultHeaders(),
      },
      body: JSON.stringify({ reason }),
    });
  } catch (err) {
    return {
      ok: false,
      error: {
        kind: "generic",
        message: err instanceof Error ? err.message : "Network error",
      },
    };
  }

  if (response.ok) {
    return { ok: true };
  }

  try {
    const problem = (await response.json()) as CancelMembershipProblemDetails;
    if (problem.errorCode === "subscription_externally_managed") {
      return {
        ok: false,
        error: {
          kind: "externally_managed",
          store: problem.store ?? null,
          message: problem.detail ?? "Managed externally.",
        },
      };
    }
    if (problem.errorCode === "team_order_subscription") {
      return {
        ok: false,
        error: {
          kind: "team_order",
          message: problem.detail ?? "Team order.",
        },
      };
    }
    return {
      ok: false,
      error: {
        kind: "generic",
        message:
          problem.detail ?? `${response.status} ${response.statusText}`.trim(),
      },
    };
  } catch {
    return {
      ok: false,
      error: {
        kind: "generic",
        message: `${response.status} ${response.statusText}`.trim(),
      },
    };
  }
}

export type ResumeMembershipResult =
  | { ok: true }
  | { ok: false; error: { kind: "generic"; message: string } };

/**
 * Clears a pending cancellation so the membership keeps renewing. Takes no body and charges
 * nothing — the only other way back was to pick a DIFFERENT plan, which un-cancels as a side
 * effect of the price swap and invoices a proration for it.
 *
 * Idempotent server-side: resuming a subscription that is not scheduled to cancel succeeds as a
 * no-op, so a double click can't fail the second time. Every failure is `generic` on purpose —
 * a member has no action to take on "somebody else pays for this" beyond contacting whoever does,
 * and the BE already logs the precise reason.
 */
export async function resumeMembership(): Promise<ResumeMembershipResult> {
  const env = getEnv();
  const apiUrl = `${env.apiBaseUrl}${MEMBER_RESUME_MEMBERSHIP_ENDPOINT}`;

  let response: Response;
  try {
    response = await fetch(apiUrl, {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        ...defaultHeaders(),
      },
    });
  } catch (err) {
    return {
      ok: false,
      error: {
        kind: "generic",
        message: err instanceof Error ? err.message : "Network error",
      },
    };
  }

  if (response.ok) {
    return { ok: true };
  }

  try {
    const problem = (await response.json()) as { detail?: string };
    return {
      ok: false,
      error: {
        kind: "generic",
        message:
          problem.detail ?? `${response.status} ${response.statusText}`.trim(),
      },
    };
  } catch {
    return {
      ok: false,
      error: {
        kind: "generic",
        message: `${response.status} ${response.statusText}`.trim(),
      },
    };
  }
}

/**
 * Structured failures the redeem-discount FE distinguishes (GROW-328). `invalid_code` is the BE's
 * `errorCode: "invalid_discount_code"` (unknown / expired / usage exhausted / not applicable to the
 * member's plan / already-active discount) — shown as the inline "invalid or expired code" message.
 * `already_used` is `errorCode: "discount_code_already_used"`: a once-per-member code (GROW-386)
 * this member already used at checkout or in My Account.
 * `not_paid_user` means the BE found no Stripe membership subscription. Everything else is `generic`.
 */
export type RedeemDiscountError =
  | { kind: "invalid_code"; message: string }
  | { kind: "already_used"; message: string }
  | { kind: "not_paid_user"; message: string }
  | { kind: "generic"; message: string };

export type RedeemDiscountResult =
  | { ok: true; data: RedeemDiscountResponse }
  | { ok: false; error: RedeemDiscountError };

export interface RedeemDiscountResponse {
  code: string;
  summary: string;
  discountedAmountMinor: number;
  fullAmountMinor: number;
  currency: string;
  remainingDiscountedPeriods: number;
}

interface RedeemDiscountProblemDetails {
  detail?: string;
  errorCode?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** Runtime shape-check for the success payload so a malformed body can't be assumed valid. */
function isRedeemDiscountResponse(
  value: unknown
): value is RedeemDiscountResponse {
  if (!isRecord(value)) return false;
  return (
    typeof value.code === "string" &&
    typeof value.summary === "string" &&
    typeof value.discountedAmountMinor === "number" &&
    typeof value.fullAmountMinor === "number" &&
    typeof value.currency === "string" &&
    typeof value.remainingDiscountedPeriods === "number"
  );
}

/** Safely reads ProblemDetails fields, keeping only string values (ignores any other JSON shape). */
function readProblemDetails(value: unknown): RedeemDiscountProblemDetails {
  if (!isRecord(value)) return {};
  return {
    errorCode:
      typeof value.errorCode === "string" ? value.errorCode : undefined,
    detail: typeof value.detail === "string" ? value.detail : undefined,
  };
}

/**
 * Redeems a discount code against the member's active subscription. On success the discount is
 * attached in Stripe and takes effect from the next renewal; the caller should refetch entitlements
 * to render the applied state (discounted price + remaining periods).
 */
export async function redeemSubscriptionDiscount(
  code: string
): Promise<RedeemDiscountResult> {
  const env = getEnv();
  const apiUrl = `${env.apiBaseUrl}${MEMBER_REDEEM_DISCOUNT_ENDPOINT}`;

  let response: Response;
  try {
    response = await fetch(apiUrl, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...defaultHeaders(),
      },
      body: JSON.stringify({ code }),
    });
  } catch (err) {
    return {
      ok: false,
      error: {
        kind: "generic",
        message: err instanceof Error ? err.message : "Network error",
      },
    };
  }

  if (response.ok) {
    let data: unknown;
    try {
      data = await response.json();
    } catch {
      return {
        ok: false,
        error: { kind: "generic", message: "Unexpected empty response." },
      };
    }
    if (!isRedeemDiscountResponse(data)) {
      return {
        ok: false,
        error: { kind: "generic", message: "Unexpected response." },
      };
    }
    return { ok: true, data };
  }

  try {
    const problem = readProblemDetails(await response.json());
    if (problem.errorCode === "invalid_discount_code") {
      return {
        ok: false,
        error: {
          kind: "invalid_code",
          message: problem.detail ?? "This code is invalid or has expired.",
        },
      };
    }
    if (problem.errorCode === "discount_code_already_used") {
      return {
        ok: false,
        error: {
          kind: "already_used",
          message:
            problem.detail ??
            "This code has already been used on your account.",
        },
      };
    }
    if (problem.errorCode === "not_paid_user") {
      return {
        ok: false,
        error: {
          kind: "not_paid_user",
          message: problem.detail ?? "No active subscription found.",
        },
      };
    }
    return {
      ok: false,
      error: {
        kind: "generic",
        message:
          problem.detail ?? `${response.status} ${response.statusText}`.trim(),
      },
    };
  } catch {
    return {
      ok: false,
      error: {
        kind: "generic",
        message: `${response.status} ${response.statusText}`.trim(),
      },
    };
  }
}

/**
 * Removes the renewal discount from the member's active subscription — reverts the recurring price to
 * full (from the next cycle) and clears the discount. The caller should refetch entitlements after.
 */
export async function removeSubscriptionDiscount(): Promise<
  { ok: true } | { ok: false; error: RedeemDiscountError }
> {
  const env = getEnv();
  const apiUrl = `${env.apiBaseUrl}${MEMBER_REMOVE_DISCOUNT_ENDPOINT}`;

  let response: Response;
  try {
    response = await fetch(apiUrl, {
      method: "DELETE",
      credentials: "include",
      headers: {
        Accept: "application/json",
        ...defaultHeaders(),
      },
    });
  } catch (err) {
    return {
      ok: false,
      error: {
        kind: "generic",
        message: err instanceof Error ? err.message : "Network error",
      },
    };
  }

  if (response.ok) {
    return { ok: true };
  }

  try {
    const problem = readProblemDetails(await response.json());
    return {
      ok: false,
      error: {
        kind:
          problem.errorCode === "not_paid_user" ? "not_paid_user" : "generic",
        message:
          problem.detail ?? `${response.status} ${response.statusText}`.trim(),
      },
    };
  } catch {
    return {
      ok: false,
      error: {
        kind: "generic",
        message: `${response.status} ${response.statusText}`.trim(),
      },
    };
  }
}
